import type { CurrencyTotals, InvoiceListSummary } from "@fiscalo/shared-types";
import { motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import { formatDashboardCurrency } from "@/lib/currencyFormat";
import { cn } from "@/lib/utils";

interface InvoicesSummaryCardProps {
  summary: InvoiceListSummary;
  periodLabel: string;
}

/**
 * Issued-invoices hero card. Visual language ported from the
 * estado-fiscal SaludFiscalHero (.ef-hero) so the page feels of a
 * piece with the dashboard: ink-900 warm-black surface, accent-300
 * mono eyebrow with the leading horizontal dash, JetBrains Mono for
 * all figures, accent radial glow. The headline is a single UYU figure
 * — every invoice converted to UYU at its CFE exchange rate — with the
 * native-currency breakdown rendered below it. The rejected-invoice
 * total is excluded because rejected CFEs have no fiscal effect.
 */
export function InvoicesSummaryCard({
  summary,
  periodLabel,
}: InvoicesSummaryCardProps): JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const breakdown = nativeBreakdown(summary.totalByCurrencyExcludingRejected);
  const hasForeign = breakdown.some((c) => c.code !== "UYU" && c.value > 0);

  const counters: Array<{ label: string; value: string }> = [
    {
      label: t("invoices.list.summaryCard.sentLabel", "Enviadas"),
      value: String(summary.userStatusBreakdown.sent),
    },
    {
      label: t("invoices.list.summaryCard.rejectedLabel", "Rechazadas"),
      value: String(summary.userStatusBreakdown.rejected),
    },
  ];
  if (summary.userStatusBreakdown.draft > 0) {
    counters.push({
      label: t("invoices.list.summaryCard.draftLabel", "Borradores"),
      value: String(summary.userStatusBreakdown.draft),
    });
  }

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="mt-4"
    >
      <HeroSurface>
        <Eyebrow
          label={t("invoices.list.summaryCard.eyebrow", "Total facturado")}
          period={periodLabel}
        />

        <Headline
          value={summary.totalUyuEquivalentExcludingRejected}
          caption={
            hasForeign
              ? t("invoices.list.summaryCard.uyuEquivalent", "UYU equivalente")
              : "UYU"
          }
        />

        {hasForeign && (
          <Breakdown
            label={t("invoices.list.summaryCard.breakdownLabel", "Detalle por moneda")}
            currencies={breakdown}
          />
        )}

        <div className="my-5 h-px bg-[var(--fx-paper)]/12" />

        <CounterRow counters={counters} />
      </HeroSurface>
    </motion.div>
  );
}

/**
 * Loading placeholder for the hero card — same ink-900 surface and
 * vertical rhythm as InvoicesSummaryCard, with pulsing bars where the
 * eyebrow, headline figure, breakdown, and counters land. Rendered while
 * the summary query is in flight so the card doesn't pop in from nothing.
 */
export function InvoicesSummaryCardSkeleton(): JSX.Element {
  return (
    <div className="mt-4">
      <HeroSurface>
        <SkeletonBar className="h-3 w-44" />
        <SkeletonBar className="mt-4 h-9 w-56 sm:h-12 sm:w-72" />
        <SkeletonBar className="mt-3 h-2.5 w-20" />

        <div className="my-5 h-px bg-[var(--fx-paper)]/12" />

        <div className="grid grid-cols-3 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="flex flex-col gap-2">
              <SkeletonBar className="h-2.5 w-16" />
              <SkeletonBar className="h-5 w-10" />
            </div>
          ))}
        </div>
      </HeroSurface>
    </div>
  );
}

function SkeletonBar({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-[var(--fx-paper)]/10",
        className,
      )}
    />
  );
}

interface ResolvedCurrency {
  symbol: string;
  code: string;
  value: number;
}

/**
 * Native-currency breakdown shown under the UYU headline. UYU is always
 * present; USD/EUR appear only when used in the period so the line reads
 * "$U 9.150 · U$S 320" rather than padding zeros.
 */
function nativeBreakdown(totals: CurrencyTotals): ResolvedCurrency[] {
  const rows: ResolvedCurrency[] = [
    { symbol: "$U", code: "UYU", value: totals.uyu },
  ];
  if (totals.usd > 0) rows.push({ symbol: "U$S", code: "USD", value: totals.usd });
  if (totals.eur > 0) rows.push({ symbol: "€", code: "EUR", value: totals.eur });
  return rows;
}

function HeroSurface({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="fx-hero-surface relative overflow-hidden rounded-[18px] px-5 py-5 shadow-[0_18px_44px_-28px_rgba(22,21,20,0.30)] sm:px-7 sm:py-6">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(55% 80% at 100% 0%, rgba(111,165,133,0.22), transparent 60%), radial-gradient(35% 60% at 0% 100%, rgba(111,165,133,0.10), transparent 60%)",
        }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

function Eyebrow({
  label,
  period,
}: {
  label: string;
  period: string;
}): JSX.Element {
  return (
    <div
      className="flex items-center gap-2 text-[10px] font-bold uppercase text-[var(--fx-accent-300)]"
      style={{
        fontFamily: "var(--fx-ff-mono)",
        letterSpacing: "0.14em",
      }}
    >
      <span
        aria-hidden
        className="inline-block h-px w-3 bg-[var(--fx-accent-300)]"
      />
      <span>{label}</span>
      <span aria-hidden className="text-[var(--fx-accent-300)]/60">
        ·
      </span>
      <span className="font-medium normal-case text-[var(--fx-paper)]/55" style={{ letterSpacing: "0.02em" }}>
        {period}
      </span>
    </div>
  );
}

/** The single big UYU figure (converted total) plus its caption. */
function Headline({
  value,
  caption,
}: {
  value: number;
  caption: string;
}): JSX.Element {
  return (
    <div className="mt-4 flex flex-col">
      <span
        className="text-4xl font-medium leading-none tracking-tight tabular-nums text-[var(--fx-paper)] sm:text-[52px]"
        style={{ fontFamily: "var(--fx-ff-mono)" }}
      >
        $U {formatDashboardCurrency(value)}
      </span>
      <span
        className="mt-2 text-[10px] font-semibold uppercase text-[var(--fx-paper)]/45"
        style={{
          fontFamily: "var(--fx-ff-mono)",
          letterSpacing: "0.16em",
        }}
      >
        {caption}
      </span>
    </div>
  );
}

/** Muted native-currency breakdown line under the headline. */
function Breakdown({
  label,
  currencies,
}: {
  label: string;
  currencies: ResolvedCurrency[];
}): JSX.Element {
  return (
    <div className="mt-4 flex flex-col gap-1.5">
      <span
        className="text-[9.5px] font-bold uppercase text-[var(--fx-accent-300)]"
        style={{ fontFamily: "var(--fx-ff-mono)", letterSpacing: "0.14em" }}
      >
        {label}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {currencies.map((c) => (
          <span
            key={c.code}
            className="text-sm tabular-nums text-[var(--fx-paper)]/70"
            style={{ fontFamily: "var(--fx-ff-mono)" }}
          >
            {c.symbol} {formatDashboardCurrency(c.value)}{" "}
            <span className="text-[10px] uppercase text-[var(--fx-paper)]/40">
              {c.code}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function CounterRow({
  counters,
}: {
  counters: Array<{ label: string; value: string }>;
}): JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-4">
      {counters.map((c) => (
        <div key={c.label} className="flex flex-col gap-1">
          <span
            className="text-[9.5px] font-bold uppercase text-[var(--fx-accent-300)]"
            style={{
              fontFamily: "var(--fx-ff-mono)",
              letterSpacing: "0.14em",
            }}
          >
            {c.label}
          </span>
          <span
            className="text-xl font-medium leading-none text-[var(--fx-paper)] tabular-nums"
            style={{ fontFamily: "var(--fx-ff-mono)" }}
          >
            {c.value}
          </span>
        </div>
      ))}
    </div>
  );
}
